/**
 * JWT Token Utilities
 *
 * Provides helper functions for decoding, validating, and managing JWT tokens
 * without external dependencies.
 */

export interface JWTPayload {
  iss?: string;
  sub?: string;
  aud?: string | string[];
  exp?: number;
  iat?: number;
  nbf?: number;
  role?: string;
  email?: string;
  [key: string]: any;
}

/**
 * Decode a JWT token without verification
 * @param token JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    // Add padding if needed
    const paddedPayload = payload + '=='.slice(0, (4 - (payload.length % 4)) % 4);
    const decoded = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param token JWT token string
 * @param bufferSeconds Optional buffer time before expiration (default: 0)
 * @returns true if token is expired or will expire within buffer time
 */
export function isJWTExpired(token: string, bufferSeconds: number = 0): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= (now + bufferSeconds);
}

/**
 * Get seconds until JWT token expires
 * @param token JWT token string
 * @returns Seconds until expiration, or 0 if expired/invalid
 */
export function getSecondsUntilExpiration(token: string): number {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return 0;
  }

  const now = Math.floor(Date.now() / 1000);
  const secondsRemaining = payload.exp - now;
  return Math.max(0, secondsRemaining);
}

/**
 * Get expiration date from JWT token
 * @param token JWT token string
 * @returns Date object or null if invalid
 */
export function getJWTExpirationDate(token: string): Date | null {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }

  return new Date(payload.exp * 1000);
}

/**
 * Get issued date from JWT token
 * @param token JWT token string
 * @returns Date object or null if invalid
 */
export function getJWTIssuedDate(token: string): Date | null {
  const payload = decodeJWT(token);
  if (!payload || !payload.iat) {
    return null;
  }

  return new Date(payload.iat * 1000);
}

/**
 * Calculate percentage of token lifetime remaining
 * @param token JWT token string
 * @returns Percentage (0-100) or 0 if expired/invalid
 */
export function getTokenLifetimePercentage(token: string): number {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp || !payload.iat) {
    return 0;
  }

  const now = Math.floor(Date.now() / 1000);
  const totalLifetime = payload.exp - payload.iat;
  const elapsed = now - payload.iat;

  if (totalLifetime <= 0) {
    return 0;
  }

  const percentageRemaining = ((totalLifetime - elapsed) / totalLifetime) * 100;
  return Math.max(0, Math.min(100, percentageRemaining));
}

/**
 * Format seconds into human-readable duration
 * @param seconds Number of seconds
 * @returns Formatted string (e.g., "45m 30s", "2h 15m")
 */
export function formatDuration(seconds: number): string {
  if (seconds <= 0) {
    return '0s';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
}

/**
 * Check if token should trigger refresh
 * @param token JWT token string
 * @param refreshThresholdPercentage Percentage of lifetime remaining to trigger refresh (default: 25%)
 * @returns true if token should be refreshed
 */
export function shouldRefreshToken(token: string, refreshThresholdPercentage: number = 25): boolean {
  const percentageRemaining = getTokenLifetimePercentage(token);
  return percentageRemaining <= refreshThresholdPercentage;
}

/**
 * Validate JWT token structure
 * @param token JWT token string
 * @returns true if token has valid structure
 */
export function isValidJWTStructure(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }

  // Try to decode payload
  const payload = decodeJWT(token);
  return payload !== null;
}

/**
 * Extract user info from JWT token
 * @param token JWT token string
 * @returns User info object or null
 */
export function extractUserInfo(token: string): { userId?: string; email?: string; role?: string } | null {
  const payload = decodeJWT(token);
  if (!payload) {
    return null;
  }

  return {
    userId: payload.sub,
    email: payload.email,
    role: payload.role,
  };
}

/**
 * Check if JWT token is valid configuration (not expired immediately)
 * @param token JWT token string
 * @returns Object with validation result and details
 */
export function validateJWTConfiguration(token: string): {
  isValid: boolean;
  hasExpiration: boolean;
  hasIssuedAt: boolean;
  lifetimeSeconds: number;
  message: string;
} {
  const payload = decodeJWT(token);

  if (!payload) {
    return {
      isValid: false,
      hasExpiration: false,
      hasIssuedAt: false,
      lifetimeSeconds: 0,
      message: 'Invalid JWT structure',
    };
  }

  const hasExpiration = typeof payload.exp === 'number';
  const hasIssuedAt = typeof payload.iat === 'number';

  if (!hasExpiration) {
    return {
      isValid: false,
      hasExpiration: false,
      hasIssuedAt,
      lifetimeSeconds: 0,
      message: 'JWT missing expiration claim (exp)',
    };
  }

  if (!hasIssuedAt) {
    return {
      isValid: false,
      hasExpiration,
      hasIssuedAt: false,
      lifetimeSeconds: 0,
      message: 'JWT missing issued-at claim (iat)',
    };
  }

  const lifetimeSeconds = payload.exp! - payload.iat!;

  if (lifetimeSeconds <= 0) {
    return {
      isValid: false,
      hasExpiration,
      hasIssuedAt,
      lifetimeSeconds,
      message: `CRITICAL: Token expires immediately (lifetime: ${lifetimeSeconds} seconds)`,
    };
  }

  if (lifetimeSeconds < 300) {
    return {
      isValid: false,
      hasExpiration,
      hasIssuedAt,
      lifetimeSeconds,
      message: `Token lifetime too short: ${lifetimeSeconds} seconds (minimum: 300 seconds / 5 minutes)`,
    };
  }

  return {
    isValid: true,
    hasExpiration,
    hasIssuedAt,
    lifetimeSeconds,
    message: `Valid token configuration (lifetime: ${formatDuration(lifetimeSeconds)})`,
  };
}
